import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import YhTable, { YhTableColumn, YhTableCellParams } from "../../../../ui/YhTable";
import YhInput from "../../../../ui/YhInput";
import {
  isTruthy,
  openErrorNotification,
} from "../../../../helpers/methods";
import { downloadCsv, getAllBulkUploade } from "./ShowAllService";
import notifiers from "../../../../global/constants/NotificationConstants";
import CustomLoader from "../../../../global/components/CustomLoader/CustomLoader";
import { store } from "../../../../utils/store";
import { applyLicenseKey } from "../../../../utils/License";
import urls from "../../../../global/constants/UrlConstants";

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const ShowAll = () => {
  useEffect(() => {
    applyLicenseKey();
  }, []);

  const privileges = store.getState().auth?.privileges || {};
  const history = useHistory();
  const [userDataSource1, setUserDataSource1] = useState<any[]>([]);
  const [apiResponse, setApiResponse] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  const [metaData, setMetaData] = useState<any>({});
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const canView =
    privileges?.["Bulk Upload Master"]?.includes("view") ||
    privileges?.["Bulk Upload Master"]?.includes("full control") ||
    privileges?.["All Lead"]?.includes("view") ||
    privileges?.["All Lead"]?.includes("full control");

  const canCreate =
    privileges?.["Bulk Upload Master"]?.includes("create") ||
    privileges?.["Bulk Upload Master"]?.includes("full control") ||
    privileges?.["All Lead"]?.includes("create") ||
    privileges?.["All Lead"]?.includes("full control");

  useEffect(() => {
    tableDataShowHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, paginationModel.page, paginationModel.pageSize]);

  const handleSearch = (newValue: string) => {
    setSearch(newValue);
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const onClickHandlerExport2 = () => {
    history.push(urls.IMPORTLEADPROJECTWISEPAGE);
  };

  const handelDownloadClick = async (imagePath: any) => {
    try {
      downloadCsv(imagePath);
    } catch {
      /* ignore */
    }
  };

  const columns: YhTableColumn[] = [
    {
      headerName: "File Name",
      field: "upload_file_name",
      minWidth: 260,
      flex: 1,
      renderCell: (params: YhTableCellParams) => {
        const name = params.row?.upload_file_name || "?";
        const initial = String(name).trim().charAt(0).toUpperCase() || "?";
        const avatarPalette = [
          { bg: "#F9F0F4", color: "#6B1630" },
          { bg: "#DBEAFE", color: "#2563EB" },
          { bg: "#D1FAE5", color: "#059669" },
          { bg: "#FFEDD5", color: "#EA580C" },
          { bg: "#FEF3C7", color: "#D97706" },
          { bg: "#FCE7F3", color: "#DB2777" },
          { bg: "#CCFBF1", color: "#0D9488" },
        ];
        let hash = 0;
        for (let i = 0; i < String(name).length; i++) {
          hash = String(name).charCodeAt(i) + ((hash << 5) - hash);
        }
        const avatar = avatarPalette[Math.abs(hash) % avatarPalette.length];

        return (
          <div className="flex items-center gap-3">
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-sm font-bold"
              style={{ backgroundColor: avatar.bg, color: avatar.color }}
            >
              {initial}
            </span>
            <div className="min-w-0 flex flex-col items-start gap-0.5">
              <span className="truncate text-sm font-semibold capitalize text-[#111827]">
                {name}
              </span>
              <span className="text-xs text-[#9CA3AF]">{params.row?.uid || ""}</span>
            </div>
          </div>
        );
      },
    },
    {
      headerName: "Status",
      field: "status",
      minWidth: 220,
      renderCell: (params: YhTableCellParams) => {
        const status = String(params.row?.status ?? "");
        if (status === "1") {
          return (
            <span className="inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-600">
              Processing
            </span>
          );
        }
        if (status === "2") {
          return (
            <span className="inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-600">
              In-progress
            </span>
          );
        }
        return (
          <div className="flex min-w-0 flex-col items-start gap-1">
            <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
              Complete
            </span>
            <span className="text-xs text-[#6B7280]">
              fail_count - {params.row?.fail_count ?? 0}
            </span>
            <span className="text-xs text-[#6B7280]">
              success_count - {params.row?.success_count ?? 0}
            </span>
            {params.row?.error_file_path ? (
              <button
                type="button"
                data-action-button="true"
                className="action-button text-xs font-semibold text-[#A02448] hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  handelDownloadClick(params.row.error_file_path);
                }}
              >
                Download Error File
              </button>
            ) : null}
          </div>
        );
      },
    },
    {
      headerName: "Date Created",
      field: "createdAt",
      minWidth: 160,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm text-[#374151]">
          {params.row?.createdAt?.split?.("T")?.[0] ||
            params.row?.createdAt ||
            "—"}
        </span>
      ),
    },
  ];

  const tableDataShowHandler = async () => {
    try {
      setApiResponse(true);
      setLoading(true);

      const payload = {
        userId: store.getState().auth.userId,
        pageNo: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search: search || "",
        formId: "",
      };

      const response = await getAllBulkUploade(payload);
      setMetaData(response?.metadata?.[0] ? response.metadata[0] : {});
      setUserDataSource1(response?.data || []);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setApiResponse(false);
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4 shrink-0">
        <div>
          <h3 className="text-xl font-bold tracking-tight text-[#111827]">
            Upload List
          </h3>
          <p className="mt-1 text-sm text-[#6B7280]">
            View all the leads from bulk upload project-wise.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[440px] w-full sm:w-[320px]">
            <YhInput
              placeholder="Search uploads…"
              value={search}
              type="search"
              onChange={(e) => handleSearch(e.target.value)}
              autoFocus={isSearchFocused}
              startAdornment={<SearchSvg />}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              containerClassName="w-full"
            />
          </div>

          {canCreate ? (
            <button
              type="button"
              onClick={() => onClickHandlerExport2()}
              className="inline-flex h-10 shrink-0 items-center gap-1 rounded-xl bg-[#A02448] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#841C39]"
            >
              Import Leads
            </button>
          ) : null}
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
        {canView ? (
          <YhTable
            columns={columns}
            rows={userDataSource1}
            getRowId={(row) => row._id}
            rowHeight={96}
            loading={loading}
            emptyMessage="No data found"
            className="h-full min-h-[280px]"
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            totalRows={metaData?.total || 0}
            onPageChange={(p) =>
              setPaginationModel((prev) => ({ ...prev, page: p }))
            }
            onPageSizeChange={(s) =>
              setPaginationModel({ pageSize: s, page: 0 })
            }
          />
        ) : null}
      </div>

      {apiResponse && <CustomLoader apiResponse={false} />}
    </div>
  );
};

export default ShowAll;
